import React, { useEffect, useRef, useState } from "react";

const Reels = () => {
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState<boolean[]>([]);
  
  const reels = [
    {
      id: 1,
      video: "/Video/facebook1.mp4",
      title: "Fresh Produce Export Process",
      description: "Behind the scenes of our agricultural export operations",
      duration: "0:45",
      views: "1.2K",
    },
    {
      id: 2,
      video: "/Video/facebook2.mp4",
      title: "Market Insights & Updates",
      description: "Latest trends in the agricultural market",
      duration: "0:38",
      views: "2.1K",
    },
    {
      id: 3,
      video: "/Video/facebook3.mp4",
      title: "Quality Control Process",
      description: "How we ensure the highest quality standards",
      duration: "0:52",
      views: "1.8K",
    },
  ];

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const reelRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize mute states
  useEffect(() => {
    setIsMuted(reels.map(() => true));
  }, []);

  const handleVideoPlay = (id: number) => {
    setPlayingVideo(id);
    // Pause other videos
    videoRefs.current.forEach((video, index) => {
      if (video && index + 1 !== id) {
        video.pause();
      }
    });
  };

  const handleVideoPause = (id: number) => {
    if (playingVideo === id) {
      setPlayingVideo(null);
    }
  };

  const toggleVideoPlayback = (index: number) => {
    const video = videoRefs.current[index];
    if (!video) return;

    if (video.paused || video.ended) {
      video.play().catch(console.error);
    } else {
      video.pause();
    }
  };

  const toggleMute = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.muted = !video.muted;
      setIsMuted(prev => {
        const newMuted = [...prev];
        newMuted[index] = video.muted;
        return newMuted;
      });
    }
  };

  // Auto-pause when scrolling away
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          } else {
            // Pause video when not visible
            const index = reelRefs.current.findIndex(ref => ref === entry.target);
            const video = videoRefs.current[index];
            if (video && !video.paused) {
              video.pause();
            }
          }
        });
      },
      {
        threshold: 0.5, // Higher threshold for better visibility detection
        rootMargin: '0px 0px -100px 0px'
      }
    );

    reelRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      reelRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Function to assign different animation directions based on index
  const getAnimationClass = (index: number) => {
    const animations = [
      'animate-slide-in-up',
      'animate-slide-in-up', 
      'animate-slide-in-up',
    ];
    return animations[index % animations.length];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-16 px-4 sm:px-6 lg:px-8">
      {/* Header Section with modern design */}
      <div className="text-center mb-16 animate-fade-in">
        <div className="inline-flex items-center justify-center mb-6">
          <div className="w-4 h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse mr-3"></div>
          <span className="text-purple-300 text-sm font-semibold tracking-widest uppercase">Latest Updates</span>
          <div className="w-4 h-4 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full animate-pulse ml-3"></div>
        </div>
        
        <h1 className="text-7xl md:text-8xl font-black text-white mb-6 leading-tight">
          OUR <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">REELS</span>
        </h1>
        
        <p className="text-xl text-purple-200 max-w-2xl mx-auto leading-relaxed">
          Immerse yourself in our visual journey through the world of agricultural excellence and global exports
        </p>

        {/* Animated gradient border */}
        <div className="mt-8 inline-block">
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent rounded-full mx-auto"></div>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-pink-500 to-transparent rounded-full mx-auto mt-1"></div>
        </div>
      </div>

      {/* Reels Grid with modern design */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {reels.map((reel, index) => (
            <div 
              key={reel.id} 
              ref={(el) => { reelRefs.current[index] = el; }}
              className={`group relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl shadow-2xl hover:shadow-purple-500/20 transition-all duration-500 transform hover:-translate-y-4 overflow-hidden opacity-0 translate-y-8 ${getAnimationClass(index)} border border-slate-700/50`}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent z-10 pointer-events-none"></div>
              
              {/* Animated background effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Video Container */}
              <div className="relative overflow-hidden rounded-t-3xl aspect-[9/16]">
                <video
                  ref={(el) => { videoRefs.current[index] = el; }}
                  src={reel.video}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loop
                  playsInline
                 
                  onClick={() => toggleVideoPlayback(index)}
                  onPlay={() => handleVideoPlay(reel.id)}
                  onPause={() => handleVideoPause(reel.id)}
                />
                
                {/* Video overlay with play button */}
                <div 
                  className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-all duration-300 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent double trigger
                    toggleVideoPlayback(index);
                  }}
                >
                  <button 
                    className={`opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300 bg-white/20 backdrop-blur-sm rounded-full p-4 border border-white/30 ${
                      playingVideo === reel.id ? 'opacity-100 scale-100' : ''
                    }`}
                  >
                    <svg 
                      className={`w-8 h-8 text-white ${playingVideo === reel.id ? 'hidden' : 'block'}`} 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                    <svg 
                      className={`w-8 h-8 text-white ${playingVideo === reel.id ? 'block' : 'hidden'}`} 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 4h4v16H6zm8 0h4v16h-4z"/>
                    </svg>
                  </button>
                </div>

                {/* Video controls */}
                <div className="absolute bottom-4 left-4 right-4 z-20 flex items-center justify-between">
                  {/* Mute button */}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMute(index);
                    }}
                    className="bg-black/50 backdrop-blur-sm rounded-full p-2 border border-white/30 transition-all duration-300 hover:bg-black/70"
                  >
                    <svg 
                      className="w-4 h-4 text-white" 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      {isMuted[index] ? (
                        <>
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                        </>
                      ) : (
                        <>
                          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                        </>
                      )}
                    </svg>
                  </button>

                  {/* Video stats */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
                      </svg>
                      <span className="text-white text-xs font-medium">{reel.duration}</span>
                    </div>

                    <div className="flex items-center space-x-1 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                      <span className="text-white text-xs font-medium">{reel.views}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="relative z-20 p-6 bg-gradient-to-t from-slate-900 to-transparent">
                <div className="mb-4">
                  <h3 className="font-bold text-white text-xl leading-tight mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-300 group-hover:to-pink-300 group-hover:bg-clip-text transition-all duration-300">
                    {reel.title}
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    {reel.description}
                  </p>
                </div>
              </div>

              {/* Hover gradient border effect */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-lg group-hover:blur-xl"></div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section with modern design */}
      <div className="text-center mt-20 animate-fade-in">
        <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl shadow-2xl p-12 max-w-4xl mx-auto border border-slate-700/50 overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400"></div>
          </div>
          
          <div className="relative z-10">
            <h2 className="text-5xl font-black text-white mb-6">
              Want <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">More?</span>
            </h2>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Follow us on social media for daily updates, behind-the-scenes content, and exclusive insights into our agricultural journey.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://www.facebook.com/sanntosh.doshi?mibextid=ZbWKwL"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg shadow-purple-500/25"
              >
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Follow on Facebook
                <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </a>
              
              <a
                href="https://www.instagram.com/sanntoshdoshi/?igshid=https%3A%2F%2Finstagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center px-8 py-4 bg-slate-700/50 hover:bg-slate-600/50 text-white font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-slate-600/50"
              >
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Follow on Instagram
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Add CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeIn 0.8s ease-out forwards;
        }

        .animate-slide-in-up {
          transition: all 0.6s ease-out;
        }

        .animate-slide-in-up.animate-in {
          animation: slideInUp 0.6s ease-out forwards;
        }

        /* Staggered animation delay for grid items */
        .animate-slide-in-up.animate-in:nth-child(1) {
          animation-delay: 0.1s;
        }
        .animate-slide-in-up.animate-in:nth-child(2) {
          animation-delay: 0.2s;
        }
        .animate-slide-in-up.animate-in:nth-child(3) {
          animation-delay: 0.3s;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #1e293b;
        }
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #8b5cf6, #ec4899);
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default Reels;